import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, KeyRound, AlertTriangle } from 'lucide-react';
import { AuthService, AuthUser } from '../services/authService';

interface SetPasswordProps {
  /** 'reset' = forgot-password link, 'invite' = new-account invitation link. */
  mode: 'reset' | 'invite';
  onSignedIn: (user: AuthUser) => void;
}

/** Shared UI for /reset-password/:token and /accept-invitation/:token — both
 * flows validate a mailed token and let the person define a password that
 * meets the same policy, then log them in automatically. */
export const SetPassword: React.FC<SetPasswordProps> = ({ mode, onSignedIn }) => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [greeting, setGreeting] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = mode === 'reset'
          ? await AuthService.validateResetToken(token)
          : await AuthService.validateInvitation(token);
        if (cancelled) return;
        setGreeting(('full_name' in info && info.full_name) || info.email || null);
      } catch {
        if (!cancelled) setInvalid(true);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      const user = mode === 'reset'
        ? await AuthService.confirmResetPassword(token, password, confirmPassword)
        : await AuthService.confirmAcceptInvitation(token, password, confirmPassword);
      onSignedIn(user);
      navigate('/', { replace: true });
    } catch (e: any) {
      setError(e.message || 'No se pudo completar la operación.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'reset' ? 'Restablecer contraseña' : 'Crea tu contraseña';

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <div className="bg-white max-w-md w-full p-8 rounded-2xl shadow-lg border border-neutral-200">
        {checking ? (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
            <p className="text-neutral-500 text-sm">Verificando enlace...</p>
          </div>
        ) : invalid ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-danger/10 text-danger rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={24} />
            </div>
            <h1 className="text-lg font-semibold text-neutral-800 mb-2">Enlace no válido</h1>
            <p className="text-sm text-neutral-500 mb-6">
              Este enlace ya expiró o ya fue usado.
              {mode === 'reset' ? ' Puedes solicitar uno nuevo.' : ' Pide a un administrador que te reenvíe la invitación.'}
            </p>
            {mode === 'reset' && (
              <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline">
                Solicitar nuevo enlace
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound size={24} />
              </div>
              <h1 className="text-xl font-semibold text-neutral-800">{title}</h1>
              {greeting && <p className="text-sm text-neutral-500 mt-2">Hola, {greeting}</p>}
              <p className="text-xs text-neutral-500 mt-2">
                Mínimo 12 caracteres, con mayúscula, minúscula, número y símbolo.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="set-password" className="block text-sm font-medium text-neutral-700 mb-1">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    id="set-password"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 pr-11 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                    required
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPass((p) => !p)}
                    aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-neutral-600">
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="set-confirm-password" className="block text-sm font-medium text-neutral-700 mb-1">
                  Confirmar contraseña
                </label>
                <input
                  id="set-confirm-password"
                  type={showPass ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary outline-none"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-[#002a5c] transition-colors disabled:opacity-60"
              >
                {submitting ? 'Guardando...' : 'Guardar y continuar'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
